/**
 * useSmartBoxList.
 * (2026-04-24, Jason Cox) Shared fetch/sort of the smartbox list used as a
 * filter dropdown source on Consumption Events, Restock Events, Device
 * Status, and Inventory. Every consumer was duplicating the same effect:
 * fetch with pageSize=500, sort alphabetically by code, set-once per
 * api-instance mount.
 *
 * Returns the plain list + a loading flag. Rendering of the <select> stays
 * with the caller because each page's surrounding filter layout is a bit
 * different and a one-size-fits-all UI component caused more friction than
 * it removed.
 */
import { useEffect, useState } from 'react';
import { useUnifiedApi } from './useUnifiedApi';
import type { SmartboxStatusRow } from '../services/unifiedApiService';

export interface UseSmartBoxListResult {
  smartboxes: SmartboxStatusRow[];
  loading: boolean;
}

export function useSmartBoxList(): UseSmartBoxListResult {
  const api = useUnifiedApi();
  const [smartboxes, setSmartboxes] = useState<SmartboxStatusRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!api || !api.getSmartboxStatusList) return;
    let cancelled = false;
    setLoading(true);
    api.getSmartboxStatusList({ page: 1, pageSize: 500 })
      .then((res) => {
        if (cancelled) return;
        const sorted = [...res.data].sort((a, b) => (a.code || '').localeCompare(b.code || ''));
        setSmartboxes(sorted);
      })
      .catch(() => {
        // Non-fatal: dropdown just stays empty. Pages can still show "no filter"
        // or re-mount to retry. Errors are already surfaced via Sentry.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [api]);

  return { smartboxes, loading };
}
